import React, { useState, useEffect } from 'react';

const AddTeamMemberModal = ({ isOpen, onClose, onAdd, roles = [], initialData = null, isEdit = false }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    phone: '',
    roleId: '',
    status: 'active'
  });

  // Update form data when initialData changes (for edit mode) or when modal opens
  useEffect(() => {
    if (isOpen) {
      if (isEdit && initialData) {
        console.log('Loading edit data:', initialData);
        
        // Extract roleId - handle both roleIds array and populated roles array
        // For radio buttons, we only need the first role
        let selectedRoleId = '';
        if (initialData.roleIds && Array.isArray(initialData.roleIds) && initialData.roleIds.length > 0) {
          const firstRoleId = initialData.roleIds[0];
          if (typeof firstRoleId === 'object' && firstRoleId !== null) {
            selectedRoleId = firstRoleId._id ? firstRoleId._id.toString() : firstRoleId.toString();
          } else {
            selectedRoleId = firstRoleId.toString();
          }
        } else if (initialData.roles && Array.isArray(initialData.roles) && initialData.roles.length > 0) {
          const firstRole = initialData.roles[0];
          if (typeof firstRole === 'object' && firstRole !== null) {
            selectedRoleId = firstRole._id ? firstRole._id.toString() : (firstRole.id ? firstRole.id.toString() : firstRole.toString());
          } else {
            selectedRoleId = firstRole.toString();
          }
        }
        
        setFormData({
          fullName: initialData.fullName || '',
          email: initialData.email || '',
          password: '', // Don't pre-fill password
          phone: initialData.phone || '',
          roleId: selectedRoleId,
          status: initialData.status || 'active'
        });
        
        console.log('Form data set:', {
          fullName: initialData.fullName || '',
          email: initialData.email || '',
          phone: initialData.phone || '',
          roleId: selectedRoleId,
          status: initialData.status || 'active'
        });
      } else {
        // Reset form for create mode
        setFormData({
          fullName: '',
          email: '',
          password: '',
          phone: '',
          roleId: '',
          status: 'active'
        });
      }
    }
  }, [isOpen, initialData, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRoleChange = (roleId) => {
    setFormData(prev => ({
      ...prev,
      roleId: roleId
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // For edit mode, password is optional
    if (isEdit) {
      if (formData.fullName && formData.email && formData.roleId) {
        // Remove password if it's empty (don't update password)
        const submitData = { ...formData };
        if (!submitData.password || submitData.password.trim() === '') {
          delete submitData.password;
        }
        // Convert roleId to roleIds array for backend compatibility
        submitData.roleIds = [submitData.roleId];
        delete submitData.roleId;
        try {
          console.log('Submitting edit data:', submitData);
          await onAdd(submitData);
          // Don't call handleCancel here - let parent component handle closing
        } catch (error) {
          console.error('Error updating staff member:', error);
          // Error is handled by parent component, don't close modal on error
          throw error;
        }
      } else {
        const errorMsg = 'Please fill in all required fields';
        console.error('Validation failed:', { formData, errorMsg });
        alert(errorMsg);
      }
    } else {
      // For create mode, password is required
      if (formData.fullName && formData.email && formData.password && formData.roleId) {
        // Convert roleId to roleIds array for backend compatibility
        const submitData = { ...formData };
        submitData.roleIds = [submitData.roleId];
        delete submitData.roleId;
        try {
          console.log('Submitting create data:', submitData);
          await onAdd(submitData);
          // Don't call handleCancel here - let parent component handle closing
        } catch (error) {
          console.error('Error creating staff member:', error);
          // Error is handled by parent component, don't close modal on error
          throw error;
        }
      } else {
        const errorMsg = 'Please fill in all required fields including password';
        console.error('Validation failed:', { formData, errorMsg });
        alert(errorMsg);
      }
    }
  };

  const handleCancel = () => {
    setFormData({
      fullName: '',
      email: '',
      password: '',
      phone: '',
      roleId: '',
      status: 'active'
    });
    onClose();
  };

  if (!isOpen) return null;

  const permissionOptions = [
    'Admin',
    'Reviewer',
    'Approver',
    'Assistant',
    'Manager',
    'Coordinator',
    'Supervisor',
    'Consultant',
    'Facilitator',
    'Administrator',
    'Director',
    'Lead'
  ];

  const statusOptions = ['Available', 'Unavailable'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-[960px] max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header - Fixed */}
        <div className="p-8 pb-4 flex-shrink-0">
          <div className="flex flex-col">
            <div className="flex items-center justify-end mb-2">
              <button
                onClick={handleCancel}
                className="text-[#73827c] hover:text-gray-900 transition-colors"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6 6L18 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
            <h2 className="text-[28px] font-semibold text-[#0a1f17] tracking-[-0.56px]">
              {isEdit ? 'Edit Team Member' : 'Add Team Member'}
            </h2>
          </div>
        </div>

        {/* Form - Scrollable */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="px-8 pb-4 space-y-6 overflow-y-auto flex-1">
              {/* Full Name */}
              <div>
                <label className="block text-sm text-[#485b54] mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-[#ebebeb] rounded-lg text-sm text-[#73827c] focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                  placeholder="Enter full name"
                  required
                />
              </div>

              {/* Email and Password */}
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-sm text-[#485b54] mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#ebebeb] rounded-lg text-sm text-[#73827c] focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                    placeholder="Enter email address"
                    required
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm text-[#485b54] mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#ebebeb] rounded-lg text-sm text-[#73827c] focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                    placeholder={isEdit ? "Leave blank to keep current password" : "Enter password"}
                    required={!isEdit}
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm text-[#485b54] mb-1">
                  Phone (Optional)
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-[#ebebeb] rounded-lg text-sm text-[#73827c] focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                  placeholder="Enter phone number"
                />
              </div>

              {/* Roles */}
              <div>
                <label className="block text-sm text-[#485b54] mb-1">
                  Roles *
                </label>
                <div className="space-y-2">
                  {roles.map((role) => {
                    const roleId = role._id || role.id;
                    const roleIdString = typeof roleId === 'object' ? roleId.toString() : String(roleId);
                    return (
                      <label key={roleIdString} className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="roleId"
                          value={roleIdString}
                          checked={formData.roleId === roleIdString}
                          onChange={() => handleRoleChange(roleIdString)}
                          className="w-5 h-5 text-emerald-600 border-gray-300 focus:ring-emerald-500 cursor-pointer"
                        />
                        <span className="text-sm text-[#73827c]">{role.name}</span>
                      </label>
                    );
                  })}
                  {roles.length === 0 && (
                    <p className="text-sm text-gray-500">No roles available</p>
                  )}
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm text-[#485b54] mb-1">
                  Status *
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-[#ebebeb] rounded-lg text-sm text-[#73827c] focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent bg-white"
                  required
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
          </div>

          {/* Action Buttons - Fixed */}
          <div className="flex items-center justify-end gap-2 p-8 pt-4 border-t border-gray-200 flex-shrink-0">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 bg-white border border-[#e7ebe9] text-[#485b54] rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-gradient-to-r from-[#40c191] to-[#5ed58d] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
            >
              {isEdit ? 'Update Team Member' : 'Invite Team Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTeamMemberModal;

